import PopupManager from "../../Common/Popups/PopupManager";
import type BaseDataSource from "../../Data/_Bases/BaseDataSource";
import { determineDataSource } from "../../Data/DataStores/DataSource";
import { type SaleCustomer, type SaleState } from "../../Reducers/SellScreen/SellScreenSaleReducerTypes";
import {
    customerFragment,
    type LocalCustomer,
    transformCustomerToIndexedDB,
} from "../../Routes/Customers/CustomerTypes";
import AssignCustomer from "../../Routes/Register/SalesHistory/AssignCustomer";
import CreateCustomer from "../../Routes/SellScreen/Dialogs/CreateCustomer";
import NotFoundException from "../../Routes/SellScreen/Exceptions/Data/NotFoundException";
import PromotionAgent from "../../Utilities/Domain/Promotions/PromotionAgent";
import {
    SALE_QUERY,
    transformGraphQLToIndexedDB,
    transformIndexedDBToSaleState,
    transformSaleStateToIndexedDB,
} from "../../Utilities/Domain/Sales/SalesUtilities";
import { type ShopfrontThunk } from "../../Utilities/Generic/ActionsHelper";
import Cloner from "../../Utilities/Generic/Cloner";
import { getOption } from "../../Utilities/Platform/Options/Options";
import Request from "../../Utilities/Platform/Request";
import { upload } from "../../Workers/Upload/UploadAgent";
import { UPLOAD_TYPES } from "../../Workers/Upload/UploadEvents";
import { showDialog } from "../Dialogs/DialogActions";
import { startLoading, stopLoading } from "../General/LayoutActions";
import {
    changeSellScreenActionMode,
    changeSellScreenSearch,
    changeSellScreenSummaryMode,
    clearSale,
    openOrderReferenceDialog,
    type SellScreenActionTypes,
} from "./SellScreenActions";
import {
    PREVIOUS_SALE_ADD_CUSTOMER,
    type PREVIOUS_SALE_PRINTED,
    SALE_ADD_CUSTOMER,
    SALE_REMOVE_CUSTOMER,
} from "./SellScreenConstants";

export interface AddCustomerAction {
    type: typeof SALE_ADD_CUSTOMER;
    customer: {
        uuid: string;
    };
}

export interface RemoveCustomerAction {
    type: typeof SALE_REMOVE_CUSTOMER;
}

export interface PreviousSaleAddCustomerAction {
    type: typeof PREVIOUS_SALE_ADD_CUSTOMER;
    customer: SaleCustomer;
}

export interface PreviousSalePrintedAction {
    type: typeof PREVIOUS_SALE_PRINTED;
}

export type CustomerActionTypes =
    AddCustomerAction |
    RemoveCustomerAction |
    PreviousSaleAddCustomerAction |
    PreviousSalePrintedAction;

/**
 * Retrieve a customer from the server
 * @param {string} id
 * @param dataSource
 * @returns {Promise<LocalCustomer | undefined>}
 */
const retrieveCustomerFromServer = async (
    id: string,
    dataSource?: BaseDataSource
): Promise<LocalCustomer | undefined> => {
    const popupId = PopupManager.create({
        message: "Loading customer from the server...",
        local  : false,
        icon   : "info",
        classes: [ "popup-info" ],
        wobble : false,
        timeout: 0,
    });

    const response = await Request.post<{
        data: {
            customer: any;
        };
        errors?: Array<unknown>;
    }>("/graphql", {
        query: `
            query GetCustomer($id: ID!) {
                customer(id: $id) {
                    ${customerFragment}
                }
            }
        `,
        variables: {
            id,
        },
    });

    PopupManager.remove(popupId);

    if(response.data.errors) {
        console.error(`Could not load customer ${id} from the server`, response.data.errors);

        return undefined;
    }

    if(!dataSource) {
        dataSource = await determineDataSource();
    }

    let customer;

    try {
        await dataSource.customers.put(transformCustomerToIndexedDB(response.data.data.customer));
        customer = await dataSource.customers.get(id);
    } catch(e) {
        console.error(`Could not insert customer ${id} into the local database`, e);
    }

    return customer;
};

/**
 * Add a customer to the current sale
 * @param uuid
 * @param move
 * @param retrieveFromServer
 * @returns {Function}
 */
export function addCustomerToSale(
    uuid: string,
    move = true,
    retrieveFromServer = false
): ShopfrontThunk<SellScreenActionTypes> {
    return async (dispatch): Promise<void> => {
        // Clear the search
        dispatch(changeSellScreenSearch("[Clear]"));

        if(move) {
            dispatch(changeSellScreenActionMode("keys"));
            dispatch(changeSellScreenSummaryMode("transaction"));
        }

        const source = await determineDataSource();
        let customer;

        try {
            customer = await source.customers.get(uuid);
        } catch(e) {
            if(!(e instanceof NotFoundException)) {
                console.error("Could not find customer due to an unexpected error", e);
                PopupManager.error("Unable to add customer due to unexpected error");

                return;
            } else if(retrieveFromServer) {
                customer = await retrieveCustomerFromServer(uuid, source);
            }
        }

        if(!customer) {
            PopupManager.error("Unable to find customer to add to the sale");

            return;
        }

        dispatch({
            type    : SALE_ADD_CUSTOMER,
            customer: {
                uuid: uuid,
            },
        });

        // Determine if we should show the order reference dialog
        try {
            if(customer && customer.require_order_reference) {
                dispatch(openOrderReferenceDialog());
            }
        } catch(e) {
            // Do nothing
            console.error("Could not determine if we should show the order reference dialog", e);
        }
    };
}

export function removeCustomerFromSale(): ShopfrontThunk<SellScreenActionTypes> {
    console.log("Attempting to remove the current customer");

    return (dispatch, getState): void => {
        const state = getState();

        const payments = state.sellscreen.sale.payments;

        for(let i = 0; i < payments.length; i++) {
            const payment = payments[i];

            if(payment.reverted) {
                continue;
            }

            if(payment.method === "account" || payment.method === "loyalty") {
                PopupManager.warning(
                    "Please remove '" + getOption("outlet_loyalty_name") +
                    "' and 'On Account' payments before removing customer"
                );

                return;
            }
        }

        if(state.sellscreen.sale.products.length === 0 && state.sellscreen.sale.totals.paid === 0) {
            dispatch(clearSale());
        } else {
            dispatch({
                type: SALE_REMOVE_CUSTOMER,
            });
        }
    };
}

export function addCustomerToHistoricSale(
    uuid: string,
    sale: SaleState | "auto" = "auto"
): ShopfrontThunk<SellScreenActionTypes> {
    return async (dispatch, getState): Promise<void> => {
        // Clear the search
        dispatch(changeSellScreenSearch("[Clear]"));

        const state = getState();

        const previous = sale === "auto";
        const saleState: SaleState | false = previous ? state.sellscreen.previousSale : sale;

        if(saleState === false) {
            PopupManager.error("There is no previous sale available to attach the customer to");

            return;
        }

        if(state.user.id === false) {
            PopupManager.error("There is no currently logged in user");

            return;
        }

        if(!state.location.register?.id) {
            PopupManager.error("There is no register available");

            return;
        }

        const source = await determineDataSource();

        // Get customer
        const customer = await source.customers.get(uuid).catch(() => null);

        if(customer && customer.loyalty) {
            // Calculate product loyalty
            const loyaltyValue = (
                await PromotionAgent.calculateLoyalty(
                    Cloner.Clone(saleState.products),
                    customer
                )
            ).reduce(
                (total, product) => total + product.loyalty.earn,
                customer.loyalty_points
            );

            await source.customers.put({
                ...customer,
                loyalty_points: loyaltyValue,
            }).catch(err => {
                console.error("Error updating local customer loyalty", err);
            });
        }

        if(previous) {
            dispatch({
                type    : PREVIOUS_SALE_ADD_CUSTOMER,
                customer: {
                    uuid,
                },
            });
        }

        saleState.customer = {
            uuid,
        };

        const transformed = transformSaleStateToIndexedDB(saleState as Required<SaleState>, {
            userId    : state.user.id,
            registerId: state.location.register.id,
        });

        let localId = saleState.id;

        if(!transformed.clientId && transformed.id.includes("-")) {
            // Check to see if the sale is already uploaded
            try {
                const uploaded = await source.sales.getByClientId(transformed.id);

                if(uploaded.length === 1) {
                    localId = uploaded[0].id;
                    transformed.id = uploaded[0].id;
                    transformed.clientId = uploaded[0].clientId;
                }
            } catch(err) {
                console.error("Failed to find local uploaded sale", err);
            }
        }

        await source.sales.put(transformed).catch(err => {
            console.error("Failed to update local sale", err);
        });

        const localSale = await source.sales.get(localId as string);

        let clientId = localSale.clientId;

        if(!clientId) {
            clientId = localSale.id;
        }

        return upload(UPLOAD_TYPES.UPDATE_SALE, {
            clientId : clientId,
            invoiceId: localSale.invoiceId,
            customer : uuid,
        })
            .then(() => {
                PopupManager.success("The customer has been assigned to the sale");
            });
    };
}

export function assignCustomerToSale(sale: string, customer: string): ShopfrontThunk<SellScreenActionTypes> {
    return (dispatch): void => {
        dispatch(startLoading());

        let popupTimeout = setTimeout(() => {
            PopupManager.info(
                "We're retrieving the current sale to assign the customer, but it's taking longer than usual, " +
                "please wait on this page"
            );

            popupTimeout = setTimeout(() => {
                PopupManager.info("We're still loading the sale, please remain on this page");
            }, 10000);
        }, 5000);

        Request.post("/graphql", {
            query: `query GetSale($id: ID!) {
                sale(id: $id) {
                    ${SALE_QUERY}
                }
            }`,
            variables: {
                id: sale,
            },
        })
            .then(response => {
                clearTimeout(popupTimeout);

                const data = response.data.data.sale;

                return transformIndexedDBToSaleState(transformGraphQLToIndexedDB(data) as any);
            })
            .then(parked => {
                dispatch(addCustomerToHistoricSale(customer, parked));
            })
            .finally(() => {
                dispatch(stopLoading());
            });
    };
}

export function createCustomer(
    onComplete?: (id: string) => void
): ReturnType<typeof showDialog> {
    return showDialog(
        "Create Customer",
        <CreateCustomer onComplete={onComplete} />,
        "question",
        false,
        false,
        true,
        false,
        false
    );
}

export function showAssignCustomer(sale: string, onSubmit: (customer: any) => void): ReturnType<typeof showDialog> {
    return showDialog(
        "Assign Customer",
        <AssignCustomer sale={sale} onSubmit={onSubmit} />,
        "question",
        false,
        false
    );
}
